import React from 'react';
import { ReusableModal } from '../modals/ReusableModal';
import { LoadingIndicator } from '../helpers/LoadingIndicator';
import { numberFormatter } from '../helpers/formatters';
import { SegmentContractOption } from '../types';

type Props = {
  isOpen: boolean;
  options: SegmentContractOption[];
  currentContractId: number | null;
  isLoading: boolean;
  isApplying: boolean;
  hasError: boolean;
  onSelect: (option: SegmentContractOption) => void;
  onUseBest: (option: SegmentContractOption) => void;
  onClose: () => void;
};

export const SegmentContractPickerModal: React.FC<Props> = ({
  isOpen,
  options,
  currentContractId,
  isLoading,
  isApplying,
  hasError,
  onSelect,
  onUseBest,
  onClose,
}) => (
  <ReusableModal
    isOpen={isOpen}
    onClose={onClose}
    header="Select segment contract"
    size="xl"
  >
    {isLoading && <LoadingIndicator />}
    {hasError && (
      <div className="alert alert-danger mb-0">
        Contract options could not be loaded.
      </div>
    )}
    {!isLoading && !hasError && options.length === 0 && (
      <div className="text-muted">No applicable contracts are available.</div>
    )}
    {!isLoading && !hasError && options.length > 0 && (
      <div className="table-responsive">
        <table className="table table-sm table-hover align-middle mb-0">
          <thead>
            <tr>
              <th>Contract</th>
              <th>Type</th>
              <th className="text-end">Commodity</th>
              <th className="text-end">Fuel %</th>
              <th className="text-end">Contract MDQ</th>
              <th className="text-end">Path Flow</th>
              <th className="text-end">Profit / Dth</th>
              <th className="text-end">Profit</th>
              <th aria-label="Selection" />
            </tr>
          </thead>
          <tbody>
            {options.map((option, index) => {
              const isCurrent =
                option.contract.contract_id === currentContractId;
              return (
                <tr key={option.contract.contract_id ?? index}>
                  <td>
                    <div className="fw-semibold">
                      {option.contract.contract_name}
                    </div>
                    {option.contract.contract_k_id && (
                      <div className="small text-muted">
                        {option.contract.contract_k_id}
                      </div>
                    )}
                  </td>
                  <td>{option.contract.contract_type ?? '-'}</td>
                  <td className="text-end">{option.contract.commodity_rate}</td>
                  <td className="text-end">
                    {numberFormatter(option.contract.fuel_rate * 100, 2)}
                  </td>
                  <td className="text-end">
                    {numberFormatter(option.contract.contractual_volume, 0)}
                  </td>
                  <td className="text-end">
                    {numberFormatter(option.preview?.max_volume ?? 0, 0)}
                  </td>
                  <td className="text-end">
                    {numberFormatter(option.preview?.profit_per_dth ?? 0, 3)}
                  </td>
                  <td className="text-end">
                    {numberFormatter(option.preview?.profit ?? 0, 0)}
                  </td>
                  <td className="text-end text-nowrap">
                    {index === 0 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary me-2"
                        disabled={!option.preview || isApplying}
                        onClick={() => onUseBest(option)}
                      >
                        Use best
                      </button>
                    )}
                    <button
                      type="button"
                      className={`btn btn-sm ${
                        isCurrent ? 'btn-secondary' : 'btn-primary'
                      }`}
                      disabled={!option.preview || isCurrent || isApplying}
                      onClick={() => onSelect(option)}
                    >
                      {isCurrent
                        ? 'Current'
                        : isApplying
                          ? 'Applying...'
                          : 'Select'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </ReusableModal>
);
